import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Card, Button, SignatureCanvas } from '../components';
import api from '../services/api';

export const EmployerContractPage: React.FC = () => {
  const { t } = useTranslation('employer');
  useCockpitTitle(t('contract.title'), t('contract.subtitle'));
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [signatureData, setSignatureData] = useState<string>('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [contractSigned, setContractSigned] = useState(false);
  const [existingContract, setExistingContract] = useState<any>(null);
  const [serverContractText, setServerContractText] = useState('');

  useEffect(() => {
    checkExistingContract();
    // De contracttekst leeft uitsluitend server-side
    api.get('/employer/onboarding/contract')
      .then((res) => setServerContractText(res.data.data?.contract_text || ''))
      .catch(() => {});
  }, [user]);

  const checkExistingContract = async () => {
    try {
      const response = await api.get('/employer/contract');
      if (response.data.success && response.data.data) {
        setExistingContract(response.data.data);
        setContractSigned(true);
      }
    } catch (error: any) {
      // Contract doesn't exist yet, which is fine
      if (error.response?.status !== 404) {
        console.error('Error checking contract:', error);
      }
    }
  };

  const handleSaveSignature = (data: string) => {
    setSignatureData(data);
  };

  const handleClearSignature = () => {
    setSignatureData('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!signatureData || !agreedToTerms) {
      alert(t('contract.signAndAgreeRequired'));
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/employer/contract/sign', {
        signature_data: signatureData,
        agrees_to_terms: agreedToTerms,
      });

      if (response.data.success) {
        setContractSigned(true);
        setExistingContract(response.data.data);
        alert(t('contract.signSuccess'));

        // Redirect to dashboard or profile completion
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Error signing contract:', error);
      alert(error.response?.data?.message || t('contract.signError'));
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.role !== 'opdrachtgever') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10">
        <Card>
          <p className="text-sm text-gray-700">
            {t('contract.employerOnly')}
          </p>
        </Card>
      </div>
    );
  }

  if (contractSigned && existingContract) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10">
        <Card>
          <div className="text-center py-8">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('contract.alreadySigned')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('contract.signedOn', {
                date: new Date(existingContract.signed_at).toLocaleDateString('nl-NL', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })
              })}
            </p>

            {existingContract.signature_data?.startsWith?.('data:image') && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">{t('contract.yourSignature')}:</h3>
                <div className="inline-block border-2 border-gray-300 rounded p-4 bg-white">
                  <img
                    src={existingContract.signature_data}
                    alt={t('contract.signatureAlt')}
                    className="max-w-md h-auto"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/dashboard')}>
                {t('contract.toDashboard')}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/employer/profile')}
              >
                {t('contract.manageProfile')}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      <div className="grid grid-cols-1 gap-8">
        {/* Contract Text */}
        <Card>
          <div className="prose max-w-none">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">
                {serverContractText || '...'}
              </pre>
            </div>
          </div>
        </Card>

        {/* Signature Section */}
        <Card>
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('contract.digitalSignature')}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {t('contract.signatureInstructions')}
                </p>

                <SignatureCanvas
                  onSave={handleSaveSignature}
                  onClear={handleClearSignature}
                  width={600}
                  height={200}
                  disabled={loading}
                />
              </div>

              <div className="border-t pt-6">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    disabled={loading}
                  />
                  <span className="ml-3 text-sm text-gray-700">
                    {t('contract.agreeStatement', { name: `${user.first_name} ${user.last_name}` })}
                  </span>
                </label>
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-gray-600">
                  <p>{t('contract.dateLabel')}: {new Date().toLocaleDateString('nl-NL', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}</p>
                  <p>{t('contract.nameLabel')}: {user.first_name} {user.last_name}</p>
                  <p>{t('contract.emailLabel')}: {user.email}</p>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                    disabled={loading}
                  >
                    {t('contract.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    loading={loading}
                    disabled={!signatureData || !agreedToTerms || !serverContractText}
                  >
                    {t('contract.sign')}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </Card>

        {/* Info Box */}
        <Card className="bg-blue-50 border-blue-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                {t('contract.importantInfo')}
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc pl-5 space-y-1">
                  <li>{t('contract.infoOnce')}</li>
                  <li>{t('contract.infoAccess')}</li>
                  <li>{t('contract.infoMultiple')}</li>
                  <li>{t('contract.infoLegal')}</li>
                  <li>{t('contract.infoConfirmation')}</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
